import { z } from 'zod';
import { INVOICE_STATUS, PAYMENT_METHOD, PAYMENT_STATUS } from '../enums.js';
import { EntityMetaSchema, LineItemSchema, zEnum, zNonEmpty, zPercent } from './common.js';

export const InvoiceSchema = EntityMetaSchema.extend({
  number: z.string(),
  customerId: z.string(),
  jobId: z.string().optional(),
  estimateId: z.string().optional(),
  status: zEnum(INVOICE_STATUS).default('draft'),
  lineItems: z.array(LineItemSchema).default([]),
  taxRate: zPercent.default(0),
  discountCents: z.number().int().min(0).default(0),
  depositAppliedCents: z.number().int().min(0).default(0),
  dueDate: z.string().optional(), // ISO date
  terms: z.string().max(8000).optional(),
  internalNotes: z.string().max(4000).optional(),
  // Lifecycle
  sentAt: z.string().optional(),
  viewedAt: z.string().optional(),
  paidAt: z.string().optional(),
  voidedAt: z.string().optional(),
  // Server-computed (cents)
  subtotalCents: z.number().int().default(0),
  taxCents: z.number().int().default(0),
  totalCents: z.number().int().default(0),
  amountPaidCents: z.number().int().default(0),
  balanceCents: z.number().int().default(0),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const CreateInvoiceSchema = z.object({
  customerId: zNonEmpty,
  jobId: z.string().optional(),
  estimateId: z.string().optional(),
  lineItems: z.array(LineItemSchema).min(1, 'Add at least one line item'),
  taxRate: zPercent.optional(),
  discountCents: z.number().int().min(0).optional(),
  dueDate: z.string().optional(),
  terms: z.string().max(8000).optional(),
  internalNotes: z.string().max(4000).optional(),
});
export type CreateInvoiceInput = z.infer<typeof CreateInvoiceSchema>;
export const UpdateInvoiceSchema = CreateInvoiceSchema.partial().omit({ customerId: true });
export type UpdateInvoiceInput = z.infer<typeof UpdateInvoiceSchema>;

/** A payment against an invoice (or a standalone deposit). */
export const PaymentSchema = EntityMetaSchema.extend({
  customerId: z.string(),
  invoiceId: z.string().optional(),
  method: zEnum(PAYMENT_METHOD),
  status: zEnum(PAYMENT_STATUS).default('pending'),
  amountCents: z.number().int().positive(),
  refundedCents: z.number().int().min(0).default(0),
  // Provider references — NEVER raw card data.
  provider: z.string().default('stripe'),
  providerPaymentIntentId: z.string().optional(),
  providerChargeId: z.string().optional(),
  last4: z.string().length(4).optional(),
  brand: z.string().max(30).optional(),
  receiptKey: z.string().optional(),
  failureReason: z.string().max(500).optional(),
  isDeposit: z.boolean().default(false),
});
export type Payment = z.infer<typeof PaymentSchema>;

/** Record a manual (cash/check) payment — office collects in person. */
export const RecordManualPaymentSchema = z.object({
  invoiceId: zNonEmpty,
  method: zEnum(['cash', 'check', 'other']),
  amountCents: z.number().int().positive('Amount must be greater than zero'),
  reference: z.string().max(120).optional(), // check number, etc.
});
export type RecordManualPaymentInput = z.infer<typeof RecordManualPaymentSchema>;

/** Create a Stripe PaymentIntent for card/ACH (returns client secret). */
export const CreatePaymentIntentSchema = z.object({
  invoiceId: zNonEmpty,
  amountCents: z.number().int().positive().optional(), // default = invoice balance
  savePaymentMethod: z.boolean().optional(),
});
export type CreatePaymentIntentInput = z.infer<typeof CreatePaymentIntentSchema>;

export const RefundPaymentSchema = z.object({
  amountCents: z.number().int().positive('Refund amount must be greater than zero'),
  reason: z.string().max(500).optional(),
});
export type RefundPaymentInput = z.infer<typeof RefundPaymentSchema>;
